/**
 * ImpactContributionGraph (IMP-1)
 *
 * Live contribution tracker for the Impact dashboard. Charts the signed-in
 * user's cumulative IronLimb contributions over time (one-time donations +
 * recurring mission top-ups) against the app-wide community total.
 *
 * DECISION (Jul 3 2026): build the FULL graph now, not an intro placeholder.
 * When there isn't enough data to draw a meaningful line (< 2 contribution
 * events), we still render the chart frame with an empty-state overlay and
 * the user's running total, so the surface is honest but complete.
 *
 * Data: GET /api/impact/contributions (auth via global axios header).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { TrendingUp, Heart, Users, Loader2 } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const usd = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const shortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const ALLOC_LABEL = {
  research: 'Research',
  sponsor: 'Sponsor a user',
  both: 'Research + Sponsor',
  custom: 'Custom',
};

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md border border-enf-surface-border bg-enf-surface-deep/95 px-3 py-2 text-xs shadow-lg">
      <div className="text-enf-text-secondary">{p.fullDate}</div>
      <div className="font-semibold text-enf-text-primary">{usd(p.cumulative)} total</div>
      <div className="text-enf-text-muted">+{usd(p.amount)} · {p.type === 'recurring' ? 'monthly top-up' : 'one-time'}</div>
    </div>
  );
};

const ImpactContributionGraph = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    axios.get(`${API}/impact/contributions`)
      .then((res) => { if (alive) { setData(res.data); setError(''); } })
      .catch(() => { if (alive) setError('load'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [user]);

  const chartData = useMemo(() => {
    const series = data?.user?.series || [];
    let running = 0;
    return series.map((pt) => {
      running += Number(pt.amount) || 0;
      return {
        ...pt,
        cumulative: Math.round(running * 100) / 100,
        label: shortDate(pt.date),
        fullDate: pt.date ? new Date(pt.date).toLocaleDateString(undefined, {
          year: 'numeric', month: 'short', day: 'numeric',
        }) : '',
      };
    });
  }, [data]);

  // Not signed in → don't render the personal tracker at all.
  if (!user) return null;

  const userTotal = data?.user?.total || 0;
  const communityTotal = data?.community?.total || 0;
  const contributors = data?.community?.contributor_count || 0;
  const byAlloc = data?.user?.by_allocation || {};
  const hasEnough = chartData.length >= 2;

  // Faint placeholder curve so the empty state still shows a chart "frame".
  const placeholder = [0, 1, 2, 3, 4, 5].map((i) => ({
    label: '', cumulative: Math.round((Math.sin(i / 1.5) + 1.2) * 4),
  }));

  return (
    <div className="mb-12">
      <div className="text-enf-accent-fg text-xs uppercase tracking-[0.3em] font-semibold mb-3 flex items-center gap-2">
        <TrendingUp className="w-3.5 h-3.5" /> Your contribution
      </div>
      <h2 className="font-display text-4xl md:text-5xl tracking-wide leading-none mb-6">
        Every dollar, tracked.
      </h2>

      {/* Stat row */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5">
          <div className="flex items-center gap-1.5 text-enf-text-muted text-xs uppercase tracking-wider mb-1">
            <Heart className="w-3.5 h-3.5 text-enf-accent-fg" /> You've given
          </div>
          <div className="font-display text-3xl md:text-4xl tracking-wide text-enf-text-primary">{usd(userTotal)}</div>
          {userTotal > 0 && (
            <div className="text-xs text-enf-text-muted mt-1">
              {usd(data?.user?.one_time_total)} one-time · {usd(data?.user?.recurring_total)} recurring
            </div>
          )}
        </div>
        <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5">
          <div className="flex items-center gap-1.5 text-enf-text-muted text-xs uppercase tracking-wider mb-1">
            <Users className="w-3.5 h-3.5 text-enf-role-info" /> Community raised
          </div>
          <div className="font-display text-3xl md:text-4xl tracking-wide text-enf-text-primary">{usd(communityTotal)}</div>
          {contributors > 0 && (
            <div className="text-xs text-enf-text-muted mt-1">{contributors} contributor{contributors === 1 ? '' : 's'}</div>
          )}
        </div>
        <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5 col-span-2 md:col-span-1">
          <div className="text-enf-text-muted text-xs uppercase tracking-wider mb-1">Your allocation</div>
          {userTotal > 0 ? (
            <div className="space-y-1 mt-1">
              {Object.entries(byAlloc).filter(([, v]) => v > 0).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-sm">
                  <span className="text-enf-text-secondary">{ALLOC_LABEL[k] || k}</span>
                  <span className="font-semibold text-enf-text-primary">{usd(v)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-enf-text-muted mt-1">No allocation yet</div>
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="relative bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5 pb-3">
        <div className="text-sm font-semibold text-enf-text-primary mb-4">Cumulative contribution over time</div>

        <div className="h-56 w-full" aria-hidden={!hasEnough}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={hasEnough ? chartData : placeholder} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="impactFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#dc2626" stopOpacity={hasEnough ? 0.35 : 0.08} />
                  <stop offset="100%" stopColor="#dc2626" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tickFormatter={(v) => `$${v}`} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} tickLine={false} axisLine={false} width={44} />
              {hasEnough && <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.15)' }} />}
              <Area
                type="monotone"
                dataKey="cumulative"
                stroke={hasEnough ? '#ef4444' : 'rgba(255,255,255,0.18)'}
                strokeWidth={2}
                fill="url(#impactFill)"
                dot={hasEnough ? { r: 3, fill: '#ef4444' } : false}
                isAnimationActive={hasEnough}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Empty-state overlay — honest "not enough data yet" without hiding the frame */}
        {!hasEnough && !loading && (
          <div className="absolute inset-0 flex items-center justify-center px-6">
            <div className="text-center max-w-sm rounded-xl border border-enf-surface-border bg-enf-surface-deep/85 backdrop-blur-sm px-6 py-5">
              <div className="font-display text-2xl tracking-wide text-enf-text-primary mb-1">{usd(userTotal)}</div>
              <p className="text-sm text-enf-text-secondary leading-relaxed">
                {userTotal > 0
                  ? 'Your contribution is logged and counting. The full chart becomes visible once Enferno has enough data to display.'
                  : 'The full chart becomes visible once Enferno has enough data to display. Whenever you choose to support the mission, it shows up right here.'}
              </p>
            </div>
          </div>
        )}

        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-5 h-5 text-enf-text-muted animate-spin" />
          </div>
        )}
      </div>

      {error && (
        <p className="text-xs text-enf-text-muted mt-2">Couldn&rsquo;t load your contribution data right now.</p>
      )}
    </div>
  );
};

export default ImpactContributionGraph;
